<template>
  <div class="media-gallery-fullscreen-main">
    <ul
      ref="localGallery"
      v-scroll="onScroll"
      class="media-gallery-fullscreen-main__list"
    >
      <li
        v-for="item in galleryItems"
        :key="item.src"
        class="media-gallery-fullscreen-main__item"
        :data-item="item.index"
      >
        <UiVideo
          v-if="item.type === 'video'"
          :src="item.src"
          width="720"
          height="870"
          :autoplay="activeItem?.index === item.index"
          :controls="true"
          playsinline
          loop
          muted
          class="media-gallery-fullscreen-main__video"
        />

        <img
          v-else-if="item.type === 'image'"
          :src="getImageFromProvider(item, { imageSize: galleryOptions?.imageSize })"
          :alt="item.alt"
          width="720"
          height="870"
          loading="lazy"
          class="media-gallery-fullscreen-main__image"
        >
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import type { UseScrollReturn } from '@vueuse/core'
import { vScroll } from '@vueuse/components'

const props = defineProps<{
  name: string
}>()

const { galleryItems, activeItem, galleryOptions, getImageFromProvider } = useUiMediaGallery(props.name)
const { settings: dialogSettings } = useUiDialog(props.name)

const localGallery = ref<HTMLElement | null>(null)

function scrollToItem(index: number, behavior: ScrollBehavior = 'smooth') {
  const elm = localGallery.value?.querySelector(`[data-item="${index}"]`) as HTMLElement | null
  localGallery.value?.scroll({ left: elm?.offsetLeft || 0, behavior })
}

// Snap to active item when fullscreen opens
watch(() => dialogSettings.value.isOpen, (isOpen) => {
  if (!isOpen || !activeItem.value) return
  nextTick(() => scrollToItem(activeItem.value!.index, 'instant'))
})

// Respond to external activeItem changes (thumbnail/navigation clicks) while fullscreen is open
watch(activeItem, (item) => {
  if (isUserScrolling || !dialogSettings.value.isOpen || !item) return
  scrollToItem(item.index)
})

let isUserScrolling = false
let timer: ReturnType<typeof setTimeout> | null = null

function onScroll(state: UseScrollReturn) {
  isUserScrolling = true
  if (timer) {
    clearTimeout(timer)
    timer = null
  }

  timer = setTimeout(function () {
    onScrollEnd(state)
    isUserScrolling = false
    timer = null
  }, 50)
}

function onScrollEnd(state: UseScrollReturn) {
  if (!localGallery.value) {
    return
  }

  const galleryWidth = localGallery.value.offsetWidth
  const scrollLeft = state.x.value
  const scrollRight = state.x.value + galleryWidth

  activeItem.value
    = galleryItems.value.find((item) => {
      const itemCenter = item.index * galleryWidth - galleryWidth / 2
      return itemCenter > scrollLeft && itemCenter < scrollRight
    }) || null
}
</script>

<style>
@import url("~layers/app/components/Ui/MediaGallery/FullscreenMain.css");
</style>
